import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Star, Pencil, Trash2, Loader2, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_LABELS = [
  ["/customers/progress", "Voortgang"],
  ["/customers/reactivation", "Heractivatie"],
  ["/customers", "Klanten"],
  ["/sales/funnel", "Salesfunnel"],
  ["/sales/statistieken", "Sales-statistieken"],
  ["/sales/leads", "Leads"],
  ["/sales/deals", "Deals"],
  ["/sales/webinars/verzendingen", "Verzendhistorie"],
  ["/sales/webinars", "Webinar-aanmeldingen"],
  ["/backoffice/suggesties", "Suggesties"],
  ["/support", "Support"],
  ["/backoffice/nieuwe-klanten", "Nieuwe klanten"],
  ["/backoffice/achterstanden", "Achterstanden"],
  ["/backoffice/abonnementen", "Abonnementen"],
  ["/backoffice/refunds", "Refunds"],
  ["/backoffice/ltv", "LTV-rapportage"],
  ["/rapportage/webinar/calls", "Calls booked"],
  ["/rapportage", "Rapportage"],
  ["/dashboard", "Dashboard"],
  ["/schatkaart", "Schatkaart"],
  ["/tasks", "Taken"],
  ["/team", "Team"],
];
const pageLabel = (path) => (PAGE_LABELS.find(([p]) => path.startsWith(p)) || [null, path])[1];

export default function SavedViews() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [team, setTeam] = useState([]);
  const [max, setMax] = useState(10);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    api.get("/saved-views").then((r) => {
      setViews(r.data.views || []); setTeam(r.data.team || []); setMax(r.data.max || 10);
    }).catch(() => {});
  }, []);

  const isCurrent = (v) => v.path === location.pathname && (v.query || "") === (location.search || "");
  const starActive = views.some(isCurrent);
  const full = views.length >= max;

  const saveCurrent = async () => {
    if (!name.trim()) { toast.error("Geef de weergave een naam"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/saved-views", {
        name: name.trim(), path: location.pathname, query: location.search || "",
      });
      setViews((v) => [...v, data]);
      setName("");
      toast.success("Weergave opgeslagen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const applyView = (v) => {
    const url = v.path + (v.query || "");
    setOpen(false);
    if (v.path === location.pathname) {
      // same page: full reload so the page re-reads its filters from the URL
      window.location.assign(url);
    } else {
      navigate(url);
    }
  };

  const startEdit = (v) => { setEditId(v.id); setEditName(v.name); };
  const saveEdit = async () => {
    const nm = editName.trim();
    if (!nm) { setEditId(null); return; }
    try {
      await api.put(`/saved-views/${editId}`, { name: nm });
      setViews((vs) => vs.map((v) => (v.id === editId ? { ...v, name: nm } : v)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hernoemen mislukt");
    }
    setEditId(null);
  };

  const toggleShare = async (v) => {
    try {
      const { data } = await api.put(`/saved-views/${v.id}`, { shared: !v.shared });
      setViews((vs) => vs.map((x) => (x.id === v.id ? { ...x, shared: data.shared } : x)));
      toast.success(data.shared ? "Weergave gedeeld met het team" : "Weergave niet meer gedeeld");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delen wijzigen mislukt");
    }
  };

  const removeView = async (v) => {
    try {
      await api.delete(`/saved-views/${v.id}`);
      setViews((vs) => vs.filter((x) => x.id !== v.id));
      toast.success("Weergave verwijderd");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="saved-views-btn"
          title="Opgeslagen weergaven"
          className={`mr-1 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
            starActive ? "text-amber-500 hover:bg-amber-50" : "text-slate-400 hover:bg-slate-100"
          }`}>
          <Star className={`h-5 w-5 ${starActive ? "fill-amber-400" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="saved-views-popover">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="font-heading font-semibold text-slate-800 text-sm">Opgeslagen weergaven</p>
          <span className="text-xs text-slate-400" data-testid="saved-views-count">{views.length}/{max}</span>
        </div>

        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-[11px] text-slate-400 mb-1.5">
            Huidige pagina: <span className="font-medium text-slate-600">{pageLabel(location.pathname)}</span>
            {location.search && <span className="text-slate-400"> · met filters</span>}
          </p>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !full && saveCurrent()}
              placeholder="Naam voor deze weergave…" className="h-8 text-sm" disabled={full}
              data-testid="saved-view-name-input" />
            <Button size="sm" className="h-8 bg-blue-700 hover:bg-blue-800 shrink-0" onClick={saveCurrent}
              disabled={busy || full} data-testid="saved-view-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Opslaan
            </Button>
          </div>
          {full && <p className="text-[11px] text-amber-600 mt-1.5">Maximum bereikt — verwijder eerst een weergave.</p>}
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {views.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-slate-400" data-testid="saved-views-empty">
              Nog geen opgeslagen weergaven.<br />Stel filters in op een pagina en sla ze hier op.
            </p>
          )}
          {views.map((v) => (
            <div key={v.id} data-testid={`saved-view-row-${v.id}`}
              className={`group flex items-center gap-2 px-4 py-2 hover:bg-slate-50 ${isCurrent(v) ? "bg-blue-50/60" : ""}`}>
              {editId === v.id ? (
                <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }}
                  onBlur={saveEdit} className="h-7 text-sm flex-1" data-testid={`saved-view-edit-input-${v.id}`} />
              ) : (
                <button onClick={() => applyView(v)} className="flex-1 text-left min-w-0" data-testid={`saved-view-apply-${v.id}`}>
                  <p className={`text-sm font-medium truncate ${isCurrent(v) ? "text-blue-700" : "text-slate-800"}`}>{v.name}</p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {pageLabel(v.path)}{v.query ? ` · ${decodeURIComponent(v.query)}` : " · geen filters"}
                  </p>
                </button>
              )}
              <button onClick={() => toggleShare(v)} data-testid={`saved-view-share-${v.id}`}
                title={v.shared ? "Gedeeld met het team — klik om te stoppen" : "Delen met het team"}
                className={`p-1 rounded transition-opacity ${v.shared
                  ? "text-blue-600 hover:text-blue-800 opacity-100"
                  : "text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}>
                <Share2 className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => startEdit(v)} title="Hernoemen" data-testid={`saved-view-rename-${v.id}`}
                className="p-1 rounded text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity">
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => removeView(v)} title="Verwijderen" data-testid={`saved-view-delete-${v.id}`}
                className="p-1 rounded text-slate-300 hover:text-red-600 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {team.length > 0 && (
            <>
              <div className="px-4 pt-2 pb-1 border-t border-slate-100 mt-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Gedeeld door het team</p>
              </div>
              {team.map((v) => (
                <div key={v.id} data-testid={`saved-view-team-row-${v.id}`}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-slate-50">
                  <button onClick={() => applyView(v)} className="flex-1 text-left min-w-0" data-testid={`saved-view-team-apply-${v.id}`}>
                    <p className="text-sm font-medium truncate text-slate-800">{v.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">
                      {pageLabel(v.path)} · door {v.owner_name}
                    </p>
                  </button>
                  <Share2 className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                </div>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
